import { type ModalProps } from '~/util';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
} from '~/primitives/sheet';
import { useAppForm } from '~/util/hooks/useAppForm';
import { formOptions } from '@tanstack/react-form';
import { useList } from '~/util/hooks/useList';
import { useApiMutation } from '~/util/hooks/useApiMutation';

import { useEffect } from 'react';
import {
    createPackTierFormInput,
    createPackTierFormSchema,
} from '~/util/forms/packtier';
import { createPacktierMutation } from '~/util/mutations/staff/packs';

export const NewPackTierSheet: React.FC<ModalProps> = ({ ...modalProps }) => {
    const list = useList();

    const mutation = useApiMutation({
        ...createPacktierMutation(),
        onSuccess: () => modalProps.onOpenChange?.(false),
    });

    const form = useAppForm(
        formOptions({
            formId: 'add-packtier-staff',
            defaultValues: {
                tier: { list },
            } as createPackTierFormInput,
            validators: {
                onChangeAsyncDebounceMs: 500,
                onChange: createPackTierFormSchema,
            },
            onSubmit: async ({ value }) => {
                mutation.mutate(value);
            },
        }),
    );

    useEffect(() => {
        form.reset({
            pack: {},
            tier: { list },
        });
    }, [list, form]);

    return (
        <Sheet {...modalProps}>
            <SheetContent className="w-full sm:w-auto sm:min-w-150 sm:max-w-150 overflow-y-scroll">
                <SheetHeader>
                    <SheetTitle>New pack tier</SheetTitle>
                    <SheetDescription>
                        Create a new pack tier. You will be able to add packs to
                        it afterwards.
                    </SheetDescription>
                </SheetHeader>
                <form
                    id="add-packtier-staff"
                    onSubmit={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        form.handleSubmit();
                    }}
                >
                    <div className="flex flex-col justify-start items-start gap-x-6 gap-y-3 p-6 pt-6">
                        <form.AppField name="tier.name">
                            {(field) => (
                                <field.TextField
                                    label="Name"
                                    required
                                    placeholder="Name of the tier"
                                />
                            )}
                        </form.AppField>
                        <form.AppField name="tier.color">
                            {(field) => (
                                <field.ColorField label="Color" required />
                            )}
                        </form.AppField>
                        <form.AppField name="tier.placement">
                            {(field) => (
                                <field.NumberField
                                    label="Placement order"
                                    required
                                    placeholder="Position of the tier among other tiers"
                                />
                            )}
                        </form.AppField>
                    </div>
                    <h3 className="text-xl font-semibold ml-4 mt-4">
                        Pack Information
                    </h3>
                    <p className="text-sm text-secondary-light ml-4 mt-1">
                        This will add a new pack within this tier after it has
                        been created.
                    </p>
                    <div className="flex flex-col justify-start items-start gap-x-6 gap-y-3 p-6 pt-6">
                        <form.AppField name="pack.name">
                            {(field) => (
                                <field.TextField
                                    label="Name"
                                    required
                                    placeholder="Name of the new pack"
                                />
                            )}
                        </form.AppField>

                        <form.AppField name="pack.levels">
                            {(field) => (
                                <field.LevelsField
                                    list={list}
                                    label="Levels"
                                    required
                                />
                            )}
                        </form.AppField>
                    </div>

                    <SheetFooter className="justify-center">
                        <div className="flex flex-col w-full gap-2">
                            <form.AppForm>
                                <form.SubscribeButton label="Create pack tier" />
                            </form.AppForm>
                        </div>
                    </SheetFooter>
                </form>
            </SheetContent>
        </Sheet>
    );
};
